import React, { useState, useEffect, useRef } from 'react';
import { Form, Spinner, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const config = require('../../config.json');

export default function AsyncAuthorAutoComplete({
  value,
  onChange,
  placeholder = "e.g. Walter Eichrodt",
  required = true,
  className = ""
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const containerRef = useRef(null);

  // Sync internal state with external value changes
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Click outside to dismiss dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
        setCursor(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced API search on author prefix (>= 2 characters)
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const { idToken } = (await fetchAuthSession()).tokens ?? {};

        const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/books`, {
          params: {
            fetchKey: trimmed.toUpperCase(),
            fetchOption: 'BookAuthor',
            clientID: 'VJCollege'
          },
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

        const books = Array.isArray(response.data) ? response.data : [];

        // Extract unique, standardized author names and calculate book count in catalogue
        const authorMap = new Map();
        books.forEach((b) => {
          const rawName = (b.bookAuthor || b.author || '').trim();
          if (rawName) {
            const normalized = rawName.toUpperCase();
            authorMap.set(normalized, (authorMap.get(normalized) || 0) + 1);
          }
        });

        const distinctAuthors = Array.from(authorMap.entries()).map(([name, count]) => ({
          name,
          count
        }));

        setSuggestions(distinctAuthors);
        setShowDropdown(distinctAuthors.length > 0);
      } catch (err) {
        console.error('Author lookup error:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (authorName) => {
    setQuery(authorName);
    onChange(authorName);
    setShowDropdown(false);
    setCursor(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showDropdown && suggestions.length > 0) {
        setShowDropdown(true);
      } else {
        setCursor((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      if (showDropdown && cursor >= 0 && cursor < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[cursor].name);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setCursor(-1);
    }
  };

  return (
    <div className={`position-relative w-100 ${className}`} ref={containerRef}>
      <InputGroup>
        <Form.Control
          required={required}
          type="text"
          className="booksform-input"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);
            onChange(val);
          }}
          onFocus={() => {
            if (suggestions.length > 0) setShowDropdown(true);
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {loading && (
          <InputGroup.Text className="bg-white border-start-0" style={{ borderLeft: 'none' }}>
            <Spinner animation="border" size="sm" variant="primary" />
          </InputGroup.Text>
        )}
      </InputGroup>

      {showDropdown && suggestions.length > 0 && (
        <div
          className="position-absolute start-0 end-0 bg-white border rounded-3 shadow-lg mt-1"
          style={{
            zIndex: 1050,
            maxHeight: '230px',
            overflowY: 'auto',
            borderColor: '#cbd5e1'
          }}
        >
          <div className="p-2 border-bottom bg-light small fw-bold text-muted d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-person-lines-fill me-1 text-primary"></i>
              Established Authors ({suggestions.length})
            </span>
            <span style={{ fontSize: '0.72rem' }}>Click or Press Enter to Fill</span>
          </div>
          <ul className="list-group list-group-flush p-0 m-0">
            {suggestions.map((item, idx) => (
              <li
                key={idx}
                className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3 ${
                  cursor === idx ? 'bg-primary text-white' : ''
                }`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleSelect(item.name)}
                onMouseEnter={() => setCursor(idx)}
              >
                <div>
                  <i className={`bi bi-person-check me-2 ${cursor === idx ? 'text-white' : 'text-success'}`}></i>
                  <strong>{item.name}</strong>
                </div>
                <span className={`badge ${cursor === idx ? 'bg-light text-dark' : 'bg-light text-secondary border'}`}>
                  {item.count} {item.count === 1 ? 'book' : 'books'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
